import React, { Component } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router";
import { NoteService, ClientService, ProjectService } from "@services";
import axios from "axios";
import utils from "@utils";
import { Select } from "@components/common";
import MaskedInput from "react-text-mask";
import { ConfirmationModal, ScrollDetect, Image } from "../../components";

import "./NoteList.scss";

const displayCount = 25;
let cancel;
const getCancelToken = c => {
	cancel = c;
};

class NoteList extends Component {
	constructor(props) {
		super(props);
		const {
			router: {
				location: { query },
			},
		} = props;
		this.state = {
			notes: [],
			pageNumber: 1,
			hasMore: true,
			filters: {
				clientId: parseInt(query?.clientId, 10) || "",
				projectId: parseInt(query?.projectId, 10) || "",
				locationId: parseInt(query?.locationId, 10) || "",
				officerId: parseInt(query?.officerId, 10) || "",
				startDate: query?.startDate || "",
				endDate: query?.endDate || "",
			},
			clients: [],
			projects: [],
			locations: [],
			clientsLoading: false,
			projectsLoading: false,
			locationsLoading: false,
			clientName: "",
			projectName: "",
			locationName: "",
		};
		this.dateMask = [/[0-1]/, /[0-9]/, "/", /[0-3]/, /[0-9]/, "/", /2/, /0/, /[2-9]/, /[0-9]/];
	}

	componentDidMount() {
		this.getClients();
	}

	componentWillUnmount() {
		if (cancel) cancel();
	}

	/** Retrieves all clients that the user can filter the invoice list by */
	getClients = () => {
		const { user } = this.props;
		const {
			filters: { includeInactive, agencyId, clientId },
		} = this.state;
		this.setState({ clientsLoading: true });

		ClientService.getAll({ includeInactive, organizationId: agencyId })
			.then(({ data }) => {
				const clients = data.map(({ Id: value, Name: label }) => ({ value, label }));
				this.setState({ clients, clientsLoading: false }, () => {
					if (clientId && !user.isCoordinator()) {
						this.getProjects();
					} else if (clientId) {
						this.getLocations();
					}
				});
			})
			.catch(() => {
				this.setState({ clients: [], clientsLoading: false });
			});
	};

	/** Retrieves all projects that the user can filter the invoice list by */
	getProjects = () => {
		const {
			filters: { clientId, includeInactive, projectId },
		} = this.state;
		this.setState({ projectsLoading: true, locations: [], projects: [] });

		ProjectService.getAll({ includeAll: includeInactive, clientId })
			.then(({ data: { ItemList } }) => {
				const projects = ItemList.map(({ Id: value, Name: label }) => ({ value, label }));
				this.setState({ projects, projectsLoading: false }, () => {
					if (projectId) {
						this.getLocations();
					}
				});
			})
			.catch(() => {
				this.setState({ projects: [], projectsLoading: false });
			});
	};

	/** Retrieves all locations that the user can filter the invoice list by.
	 * For non-coordinator users, the list of locations retrieved is based on the selected project.
	 * For coordinators, the list of locations is retrieved based on the selected client.
	 */
	getLocations = () => {
		const {
			filters: { clientId },
		} = this.state;
		this.setState({ locationsLoading: true });

		ClientService.getLocations(clientId)
			.then(({ data }) => {
				const locations = data.map(({ Id: value, Name: label }) => ({ value, label }));
				this.setState({ locations, locationsLoading: false });
			})
			.catch(() => {
				this.setState({ locations: [], locationsLoading: false });
			});
	};

	/** Retrieves a list of all notes */
	getNotes = () => {
		const { lang } = global;
		const { user } = this.props;
		const { pageNumber, filters } = this.state;
		// For non-admin users, do not retrieve notes if a clientId is not provided
		// For non-admin users, this will also prevent retrieval of all notes on initial load
		if (!user.isSuperAdmin() && filters.clientId === "") {
			this.setState({ hasMore: false, notes: [] });
			return;
		}

		let startDate;
		let endDate;
		if (this.areCustomDatesValid()) {
			startDate =
				utils.isDesktopSafari || utils.isIE
					? filters.startDate.replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$3-$1-$2")
					: filters.startDate;
			endDate =
				utils.isDesktopSafari || utils.isIE
					? filters.endDate.replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$3-$1-$2")
					: filters.endDate;
		}

		// Cleaning up query params so that unnecessary fields are not sent as part of the network request
		const queryParams = {
			pageNumber,
			displayCount,
			startDate,
			endDate,
			clientId: filters.clientId || undefined,
			projectId: filters.projectId || undefined,
			locationId: filters.locationId || undefined,
			sortBy: "StartDateTime",
		};

		if (cancel) cancel();

		NoteService.getAll({ ...queryParams }, getCancelToken)
			.then(({ data }) => {
				cancel = undefined;
				const newData = data?.ItemList ?? [];

				this.setState(state => ({
					notes: pageNumber > 1 ? [...state.notes, ...newData] : newData,
					pageNumber: state.pageNumber + 1,
					hasMore: newData.length >= displayCount,
				}));
			})
			.catch(error => {
				if (!axios.isCancel(error)) {
					this.setState({ hasMore: false });

					const { response } = error;
					utils.notify({
						type: "error",
						message: response?.data?.Message ?? lang.t("Error loading notes"),
					});
				}
			});
	};

	/**
	 * Deletes the selected note and removes it from the list
	 * @param {Number} noteId  The unique id of the notes being deleted
	 */
	deleteNote = noteId => {
		const { lang } = global;
		NoteService.delete({ id: noteId })
			.then(() => {
				utils.notify({
					type: "success",
					message: lang.t("Note successfully deleted"),
				});

				this.setState(({ notes }) => ({
					notes: notes.filter(item => item.Id !== noteId),
				}));
			})
			.catch(error => {
				this.setState({ hasMore: false });

				const { response } = error;
				utils.notify({
					type: "error",
					message: response?.data?.Message ?? lang.t("Error deleting Note"),
				});
			});
	};

	/**
	 * Displays a confirmation modal allowing the user to confirm their action before deleting a note
	 * @param {Number} noteId  The unique id of the notes being deleted
	 */
	onDelete = noteId => event => {
		if (event) event.preventDefault();

		const { lang } = global;
		this._confirmationModal.openModal({
			title: lang.t("Delete Note"),
			message: lang.t("Are you sure you want to delete this note_"),
			confirmButton: `${lang.t("Yes")}, ${lang.t("Delete Note")}`,
			onConfirm: () => this.deleteNote(noteId),
		});
	};

	handleClientChange = event => {
		const {
			filters: { clientId },
		} = this.state;
		const { router, user } = this.props;
		if (event?.value === clientId) return;

		this.setState(
			prevState => ({
				pageNumber: 1,
				hasMore: true,
				clientName: event?.label || "",
				filters: {
					...prevState.filters,
					clientId: event?.value || "",
					projectId: "",
					locationId: "",
					startDate: "",
					endDate: "",
				},
				notes: [],
				projects: [],
				locations: [],
			}),
			() => {
				if (event && !user.isCoordinator()) {
					this.getProjects();
				} else if (event) {
					this.getLocations();
				}
				// In case ScrollDetect is already visible in the viewport,
				// make new ones immediately (cancel tokens will cancel existing requests)
				router.push(this.buildNewUrl());
			},
		);
	};

	handleProjectChange = event => {
		const {
			filters: { projectId },
		} = this.state;
		const { router } = this.props;
		if (event?.value === projectId) return;

		this.setState(
			prevState => ({
				pageNumber: 1,
				hasMore: true,
				projectName: event?.label || "",
				filters: {
					...prevState.filters,
					projectId: event?.value || "",
					locationId: "",
				},
				notes: [],
				locations: [],
			}),
			() => {
				this.getLocations();
				// In case ScrollDetect is already visible in the viewport,
				// make new ones immediately (cancel tokens will cancel existing requests)
				router.push(this.buildNewUrl());
			},
		);
	};

	handleLocationChange = event => {
		const {
			filters: { locationId },
		} = this.state;
		const { router } = this.props;
		if (event?.value === locationId) return;

		this.setState(
			prevState => ({
				pageNumber: 1,
				hasMore: true,
				locationName: event?.label || "",
				filters: {
					...prevState.filters,
					locationId: event?.value || "",
				},
				notes: [],
			}),
			() => {
				// In case ScrollDetect is already visible in the viewport,
				// make new ones immediately (cancel tokens will cancel existing requests)
				router.push(this.buildNewUrl());
			},
		);
	};

	/**
	 * Updates the value of start and end date inputs when the custom date range filter is selected
	 * @param {'startDate'|'endDate'} property The filter property to update
	 * @param {Event} event The HTML event handled by this method
	 */
	handleDateInputChange = (property, { target: { value } }) => {
		const { router } = this.props;
		// For date inputs on iOS, all '/' characters need to be replaced with '-'
		const formattedValue = utils.isIOSDevice ? value.replace(/\//gi, "-") : value;
		this.setState(
			prevState => ({ filters: { ...prevState.filters, [property]: formattedValue } }),
			() => {
				if (this.areCustomDatesValid()) {
					this.setState(
						{
							pageNumber: 1,
							notes: [],
							hasMore: true,
						},
						() => {
							// In case ScrollDetect is already visible in the viewport,
							// make new ones immediately (cancel tokens will cancel existing requests)
						},
					);
				}
				router.push(this.buildNewUrl());
			},
		);
	};

	/** Verifies that the date values provided in the custom date range inputs are valid based on which browser is being used */
	areCustomDatesValid = () => {
		const {
			filters: { startDate, endDate },
		} = this.state;

		// By default, we will test both dates against a yyyy-mm-dd format
		let dateRegex = /(\d{4})-(\d{2})-(\d{2})/;
		if (utils.isDesktopSafari || utils.isIE) {
			// If all previous checks are met on Safari/IE, we should test against a mm/dd/yyyy format
			dateRegex = /(\d{2})\/(\d{2})\/(\d{4})/;
		}

		return dateRegex.test(startDate) || dateRegex.test(endDate);
	};

	/** Builds the new URL to be shown in the address bar, eliminating empty and redundant where possible query string values */
	buildNewUrl = () => {
		const { filters } = this.state;
		let startDate;
		let endDate;
		const queryParams = {};
		if (this.areCustomDatesValid()) {
			startDate =
				utils.isDesktopSafari || utils.isIE
					? filters.startDate.replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$3-$1-$2")
					: filters.startDate;
			endDate =
				utils.isDesktopSafari || utils.isIE
					? filters.endDate.replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$3-$1-$2")
					: filters.endDate;
		}
		if (filters.clientId) queryParams.clientId = filters.clientId;
		if (filters.projectId) queryParams.projectId = filters.projectId;
		if (filters.locationId) queryParams.locationId = filters.locationId;
		if (startDate) queryParams.startDate = startDate;
		if (endDate) queryParams.endDate = endDate;

		const queryString = new URLSearchParams(queryParams).toString();
		return `/notes${queryString !== "" ? `?${queryString}` : ""}`;
	};

	render() {
		const { lang } = global;
		const {
			notes,
			hasMore,
			filters,
			clients,
			projects,
			locations,
			clientsLoading,
			projectsLoading,
			locationsLoading,
			clientName,
			projectName,
			locationName,
		} = this.state;
		const { user } = this.props;

		return (
			<div
				id="notes-list"
				className="container"
			>
				<div className="row v-align form-group">
					<div className="col-xs-6 col-sm-10">
						<h2>{lang.t("Notes")}</h2>
					</div>
					<div className="col-xs-6 col-sm-2 text-right">
						<button
							type="button"
							className="btn btn-primary hidden-print"
							onClick={() => window.print()}
						>
							{lang.t("Print")}
						</button>
					</div>
					<div className="col-xs-12 visible-print">
						<If condition={filters.clientId !== ""}>
							<strong>{`${lang.t("Client")}: ${clientName}`}</strong>
							&nbsp;
						</If>
						<If condition={filters.projectId !== "" && !user.isCoordinator()}>
							<strong>{`${lang.t("Project")}: ${projectName}`}</strong>
							&nbsp;
						</If>
						<If condition={filters.locationId !== ""}>
							<strong>{`${lang.t("Location")}: ${locationName}`}</strong>
							&nbsp;
						</If>
						<If condition={filters.startDate !== "" && filters.endDate !== ""}>
							<strong>
								{`${utils.formatDate(new Date(`${filters.startDate} 00:00`), "en-US", user.TimeZoneId, "2-digit")} 
								- ${utils.formatDate(new Date(`${filters.endDate} 00:00`), "en-US", user.TimeZoneId, "2-digit")}`}
							</strong>
						</If>
					</div>
				</div>
				<div className="note-filters hidden-print">
					<div className="row">
						<div className="col-sm-4 form-group">
							<label htmlFor="client">{lang.t("Client")}</label>
							<Select
								id="client"
								name="client"
								value={filters.clientId}
								options={clients}
								onChange={this.handleClientChange}
								isLoading={clientsLoading}
								isClearable
							/>
						</div>
						<If condition={!user.isCoordinator()}>
							<div className="col-sm-4 form-group">
								<label htmlFor="project">{lang.t("Project")}</label>
								<Select
									id="project"
									name="project"
									value={filters.projectId}
									options={projects}
									onChange={this.handleProjectChange}
									isLoading={projectsLoading}
									isClearable
								/>
							</div>
						</If>
						<div className="col-sm-4 form-group">
							<label htmlFor="location">{lang.t("Location")}</label>
							<Select
								id="location"
								name="location"
								value={filters.locationId}
								options={locations}
								onChange={this.handleLocationChange}
								isLoading={locationsLoading}
								isClearable
							/>
						</div>
						<div className="col-sm-4 form-group">
							<label htmlFor="startDate">{lang.t("Start Date")}</label>
							<Choose>
								<When condition={utils.isDesktopSafari || utils.isIE}>
									<MaskedInput
										id="startDate"
										name="startDate"
										type="text"
										mask={this.dateMask}
										className="form-control"
										placeholder="mm/dd/yyyy"
										max={utils.formatDate(
											undefined,
											undefined,
											undefined,
											"noslash",
										)}
										value={filters.startDate}
										onChange={event =>
											this.handleDateInputChange("startDate", event)
										}
									/>
								</When>
								<Otherwise>
									<input
										id="startDate"
										name="startDate"
										type="date"
										className="form-control"
										max={utils.formatDate(
											undefined,
											undefined,
											undefined,
											"noslash",
										)}
										value={filters.startDate}
										onChange={event =>
											this.handleDateInputChange("startDate", event)
										}
									/>
								</Otherwise>
							</Choose>
						</div>
						<div className="col-sm-4 form-group">
							<label htmlFor="endDate">{lang.t("End Date")}</label>
							<Choose>
								<When condition={utils.isDesktopSafari || utils.isIE}>
									<MaskedInput
										id="endDate"
										name="endDate"
										type="text"
										mask={this.dateMask}
										className="form-control"
										placeholder="mm/dd/yyyy"
										max={utils.formatDate(
											undefined,
											undefined,
											undefined,
											"noslash",
										)}
										value={filters.endDate}
										onChange={event =>
											this.handleDateInputChange("endDate", event)
										}
									/>
								</When>
								<Otherwise>
									<input
										id="endDate"
										name="endDate"
										type="date"
										className="form-control"
										max={utils.formatDate(
											undefined,
											undefined,
											undefined,
											"noslash",
										)}
										value={filters.endDate}
										onChange={event =>
											this.handleDateInputChange("endDate", event)
										}
									/>
								</Otherwise>
							</Choose>
						</div>
					</div>
				</div>
				<div id="notes-table-body">
					<For
						each="item"
						of={notes}
					>
						<div
							className="row"
							key={`note_item${item.Id}`}
						>
							<div className="col-sm-6 col-xs-12">
								<div>
									<Link to={`/jobs/${item.JobEventId}`}>
										<strong>{`Job #${item.JobEventId} - ${item.JobEventName}`}</strong>
									</Link>
								</div>
								<div>{item.LocationName}</div>
								<h5>
									<strong>{lang.t("Project")}</strong>
								</h5>
								<div>{item.ProjectName}</div>
							</div>
							<div className="col-sm-6 col-xs-12">
								<h5>
									<strong>{lang.t("Organization")}</strong>
								</h5>
								<div>{`#${item.OrganizationId} - ${item.OrganizationName}`}</div>
							</div>
							<div className="col-xs-12">
								<h5>
									<strong>{item.UserName}</strong>&nbsp;@&nbsp;
									{utils.formatDate(
										`${item.CreateDateUTC}z`,
										"en-US",
										user.TimeZoneId,
										user.UserPreferences === "useMilitaryTime"
											? "dt-num-mil"
											: "dt-num",
									)}
								</h5>
							</div>
							<div className="col-xs-12 note-list-note-container">
								<div className="row">
									<If condition={item.Url && item.Url !== ""}>
										<div className="col-sm-5 col-xs-12">
											<a
												href={item.Url}
												target="_blank"
												rel="noopener noreferrer"
											>
												<Image
													className="note-img img-responsive"
													src={item.Url}
													fallbackSrc="default"
												/>
											</a>
										</div>
									</If>
									<div
										className={`col-xs-12 truncate ${item.Url && item.Url !== "" ? "col-sm-7 " : ""}`}
									>
										<h5>{item.Comment}</h5>
									</div>
								</div>
							</div>
							<div className="col-xs-12 col-md-2 col-md-offset-10 col-sm-3 col-sm-offset-9 text-right">
								<button
									type="button"
									id={`notes-list-delete-btn-${item.Id}`}
									className="btn btn-danger btn-block hidden-print"
									onClick={this.onDelete(item.Id)}
									disabled={user.IsViewOnly}
								>
									{lang.t("Delete")}
								</button>
							</div>
						</div>
					</For>
					<If condition={notes.length === 0 && !hasMore}>
						<div className="text-center text-muted">{lang.t("No notes found")}</div>
					</If>
				</div>
				<If condition={hasMore}>
					<ScrollDetect
						className="text-center"
						onVisible={this.getNotes}
					>
						<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
					</ScrollDetect>
				</If>
				<ConfirmationModal
					ref={ref => {
						this._confirmationModal = ref;
					}}
				/>
			</div>
		);
	}
}

NoteList.propTypes = {
	user: PropTypes.object,
	router: PropTypes.object,
};

NoteList.defaultProps = {
	user: {},
	router: {},
};

export default NoteList;
